import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, Space, Modal, Form, Input, Select, message, Spin } from 'antd';
import { PlusOutlined, EditOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import WidgetGrid from '../components/Dashboard/WidgetGrid';
import ReportService from '../services/reportBuilder.service';

const { Content, Header } = Layout;
const { Option } = Select;

const Dashboard = () => {
  const [widgets, setWidgets] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addWidgetModalVisible, setAddWidgetModalVisible] = useState(false);
  const [availableReports, setAvailableReports] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    loadAvailableReports();
    loadDashboardWidgets();
  }, []);

  const loadAvailableReports = async () => {
    try {
      const reports = await ReportService.getReports();
      setAvailableReports(reports.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const loadDashboardWidgets = async () => {
    setLoading(true);
    try {
      // In a real implementation, load dashboard configuration from backend
      // For now, using mock data
      const mockWidgets = [
        {
          id: '1',
          name: 'Sales Overview',
          type: 'chart',
          config: {
            dataSource: 'sample-sales-report',
            entityType: 'sales',
            chartType: 'bar',
            xAxisField: 'order_date',
            yAxisField: 'total_amount',
            size: { width: 6, height: 4 }
          }
        },
        {
          id: '2',
          name: 'Total Revenue',
          type: 'metric',
          config: {
            dataSource: 'sample-sales-report',
            entityType: 'sales',
            metricField: 'total_amount',
            aggregation: 'sum',
            title: 'Total Revenue',
            size: { width: 3, height: 2 }
          }
        },
        {
          id: '3',
          name: 'Recent Orders',
          type: 'table',
          config: {
            dataSource: 'sample-sales-report',
            entityType: 'sales',
            columns: ['order_number', 'customer_name', 'total_amount', 'order_date'],
            limit: 10,
            size: { width: 6, height: 4 }
          }
        }
      ];
      setWidgets(mockWidgets);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      message.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
  }, []);

  const handleWidgetUpdate = useCallback((widgetId, updatedWidget) => {
    setWidgets(prev => prev.map(widget =>
      widget.id === widgetId ? updatedWidget : widget
    ));
  }, []);

  const handleAddWidget = useCallback(async (values) => {
    try {
      const selectedReport = availableReports.find(r => r.id.toString() === values.reportId);

      if (!selectedReport) {
        message.error('Selected report not found');
        return;
      }

      const newWidget = {
        id: Date.now().toString(),
        name: values.name,
        type: values.type,
        config: {
          dataSource: values.reportId,
          entityType: selectedReport.entityType || 'items',
          size: { width: 6, height: 4 }
        }
      };

      setWidgets(prev => [...prev, newWidget]);
      setAddWidgetModalVisible(false);
      form.resetFields();
      message.success('Widget added successfully');
    } catch (error) {
      console.error('Error adding widget:', error);
      message.error('Failed to add widget');
    }
  }, [availableReports, form]);

  const handleDeleteWidget = useCallback((widgetId) => {
    Modal.confirm({
      title: 'Delete Widget',
      content: 'Are you sure you want to delete this widget?',
      onOk: () => {
        setWidgets(prev => prev.filter(widget => widget.id !== widgetId));
        message.success('Widget deleted');
      }
    });
  }, []);

  const handleSaveDashboard = useCallback(async () => {
    try {
      // In a real implementation, save dashboard configuration to backend
      console.log('Saving dashboard:', { widgets, layout });
      message.success('Dashboard saved successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving dashboard:', error);
      message.error('Failed to save dashboard');
    }
  }, [widgets, layout]);

  const toggleEditMode = useCallback(() => {
    if (isEditing) {
      // Ask to save changes before exiting edit mode
      Modal.confirm({
        title: 'Save Changes',
        content: 'Do you want to save your dashboard changes?',
        onOk: handleSaveDashboard,
        onCancel: () => setIsEditing(false)
      });
    } else {
      setIsEditing(true);
    }
  }, [isEditing, handleSaveDashboard]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{
          height: 'calc(100vh - 120px)',
          background: '#f0f2f5',
          padding: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#fff',
            padding: '16px 24px',
            borderBottom: '1px solid #d9d9d9',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0 }}>Dashboard</h2>
            <Space>
              <Button
                type={isEditing ? 'primary' : 'default'}
                icon={<EditOutlined />}
                onClick={toggleEditMode}
              >
                {isEditing ? 'Stop Editing' : 'Edit Dashboard'}
              </Button>
              {isEditing && (
                <>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setAddWidgetModalVisible(true)}
                  >
                    Add Widget
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveDashboard}
                  >
                    Save Dashboard
                  </Button>
                </>
              )}
            </Space>
          </div>

          <Spin spinning={loading}>
            <WidgetGrid
              widgets={widgets}
              layout={layout}
              onLayoutChange={handleLayoutChange}
              onWidgetUpdate={handleWidgetUpdate}
              onWidgetDelete={handleDeleteWidget}
              isEditing={isEditing}
            />
          </Spin>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Add Widget Modal */}
      <Modal
        title="Add Widget"
        visible={addWidgetModalVisible}
        onCancel={() => {
          setAddWidgetModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddWidget}
        >
          <Form.Item
            name="name"
            label="Widget Name"
            rules={[{ required: true, message: 'Please enter widget name' }]}
          >
            <Input placeholder="Enter widget name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Widget Type"
            rules={[{ required: true, message: 'Please select widget type' }]}
          >
            <Select placeholder="Select widget type">
              <Option value="chart">Chart Widget</Option>
              <Option value="table">Table Widget</Option>
              <Option value="metric">Metric Widget</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reportId"
            label="Data Source"
            rules={[{ required: true, message: 'Please select a report' }]}
          >
            <Select placeholder="Select report data source">
              {availableReports.map(report => (
                <Option key={report.id} value={report.id.toString()}>
                  {report.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setAddWidgetModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Widget
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Dashboard;